import React, { useState, useCallback, useEffect } from 'react';
import validator from 'validator/es';
import PropTypes from 'prop-types';
import { trackCustomEvent } from 'gatsby-plugin-google-analytics';
import queryString from 'querystring';
import useForm from '../../hooks/useForm';
import useMediaQuery from '../../hooks/useMediaQuery';
import Button from '../Button/Button';
import apiClient from '../../utils/apiClient';

const windowGlobal = typeof window !== 'undefined' && window;

const EarlyAccessModal = ({
  openTicketModal,
  setOpenTicketModal,
  location,
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [currentStep, setCurrentStep] = useState(1);

  const [currentIndex, setCurrentIndex] = useState(0);

  const [sliderActive, setSliderActive] = useState(0);

  const [loading, setLoading] = useState(false);
  const [closeConfirm, setCloseConfirm] = useState(false);

  const formConfig = {
    initialValues: {
      tickets: [{ name: '', email: '', phoneNumber: '' }],
    },
    validations: ({ tickets }) => ({
      ...[...new Array(tickets.length).keys()].reduce(
        (a, c) => ({
          ...a,
          [`tickets[${c}].name`]: {
            required: currentStep === 2 && c === currentIndex,
            message: 'Name is required.',
          },
          [`tickets[${c}].email`]: {
            required: currentStep === 2 && c === currentIndex,
            message: 'Email is required.',
            rules: [
              {
                validator: (v) =>
                  currentStep === 2 && c === currentIndex
                    ? validator.isEmail(v)
                    : true,
                message: 'This is an invalid email.',
              },
            ],
          },
          [`tickets[${c}].phoneNumber`]: {
            required: currentStep === 2 && c === currentIndex,
            message: 'Contact number is required.',
            rules: [
              {
                validator: (v) =>
                  currentStep === 2 && c === currentIndex
                    ? validator.isMobilePhone(v)
                    : true,
                message: 'This is an invalid contact number.',
              },
            ],
          },
        }),
        {},
      ),
    }),
    onSubmit: ({ tickets }) => {
      switch (currentStep) {
        case 1: {
          trackCustomEvent({
            category: 'Selecttickets_FtX2021',
            action: 'booknow',
            label: 'FTX2021',
          });
          if (windowGlobal.lintrk) {
            windowGlobal.lintrk('track', { conversion_id: 5426412 });
          }
          if (windowGlobal.fbq) {
            windowGlobal.fbq('track', 'Select_tickets-Book-FTX2021');
          }

          gotoNextStep();

          break;
        }

        case 2: {
          if (tickets.length > 1 && currentIndex + 1 !== tickets.length) {
            setCurrentIndex((state) => state + 1);
          } else {
            // submit
            trackCustomEvent({
              category: 'TicketDetails_FtX2021',
              action: 'submit',
              label: 'FTX2021',
            });
            if (windowGlobal.lintrk) {
              windowGlobal.lintrk('track', { conversion_id: 5426436 });
            }
            if (windowGlobal.fbq) {
              windowGlobal.fbq('track', 'TicketDetails-Submit-FTX2021');
            }
            setLoading(true);

            (async () => {
              try {
                const params = queryString.parse(location.search.slice(1));

                await apiClient.post('/bookingsCreate', {
                  payload: tickets.map((t) => ({
                    ...t,
                    ...(params.coupon_code
                      ? { couponCode: params.coupon_code }
                      : {}),
                    ...(params.utm_source
                      ? { utmSource: params.utm_source }
                      : {}),
                    ...(params.utm_medium
                      ? { utmMedium: params.utm_medium }
                      : {}),
                    ...(params.utm_campaign
                      ? { utmCampaign: params.utm_campaign }
                      : {}),
                    ...(params.utm_campaign_id
                      ? { utmCampaignID: params.utm_campaign_id }
                      : {}),
                  })),
                });

                gotoNextStep();
              } catch (e) {
                if (e?.response?.data) {
                  alert(e?.response?.data?.message);
                } else {
                  alert('Something went wrong, try again!');
                }
              } finally {
                setLoading(false);
              }
            })();
          }

          break;
        }

        default: {
          gotoNextStep();

          break;
        }
      }
    },
  };

  const { values, onChange, onSubmit, getErrors, reset } = useForm(formConfig);

  const { tickets } = values;

  const incrementCount = useCallback(() => {
    onChange(`tickets[${tickets.length}]`, {
      name: '',
      email: '',
      phoneNumber: '',
    });
  }, [onChange, currentIndex]);

  const decrementCount = useCallback(() => {
    onChange(
      'tickets',
      tickets.filter((_, index) => index !== currentIndex),
    );
  }, [currentIndex, tickets, onChange]);

  const handleChange = useCallback(
    ({ target: { name, value } }) => {
      onChange(name, value);

      // Enter details
      trackCustomEvent({
        category: 'TicketDetails_FtX2021',
        action: 'enter',
        label: 'FTX2021',
      });
      if (windowGlobal.lintrk) {
        windowGlobal.lintrk('track', { conversion_id: 5426428 });
      }
      if (windowGlobal.fbq) {
        windowGlobal.fbq('track', 'TicketDetails-Enter-FTX2021');
      }
    },
    [onChange],
  );

  const sliderClickHandler = useCallback((e) => {
    const { type } = e.target.closest('button').dataset;
    const sliderWrapper = e.target
      .closest('.step-3')
      .querySelector('.confirmation-block');
    const totalLength =
      sliderWrapper.children.length -
      Math.round(
        sliderWrapper.offsetWidth / sliderWrapper.children[0].offsetWidth,
      );

    setSliderActive((state) => {
      if (type === 'next') return state === totalLength ? 0 : state + 1;
      return state <= 0 ? totalLength : state - 1;
    });

    // arrows click

    trackCustomEvent({
      category: 'Success_FtX2021',
      action: 'clickarrow',
      label: 'FTX2021',
    });
    if (windowGlobal.lintrk) {
      windowGlobal.lintrk('track', { conversion_id: 5426460 });
    }
    if (windowGlobal.fbq) {
      windowGlobal.fbq('track', 'Success_ClickArrow_FTX2021');
    }
  }, []);

  const closeModalClick = useCallback(
    (e) => {
      if (e) e.preventDefault();

      switch (currentStep) {
        case 1: {
          trackCustomEvent({
            category: 'Selecttickets_FtX2021',
            action: 'close',
            label: 'FTX2021',
          });
          if (windowGlobal.lintrk) {
            windowGlobal.lintrk('track', { conversion_id: 5426420 });
          }
          if (windowGlobal.fbq) {
            windowGlobal.fbq('track', 'Select_tickets-Close-FTX2021');
          }

          break;
        }

        case 2: {
          trackCustomEvent({
            category: 'TicketDetails_FtX2021',
            action: 'close',
            label: 'FTX2021',
          });
          if (windowGlobal.lintrk) {
            windowGlobal.lintrk('track', { conversion_id: 5426444 });
          }
          if (windowGlobal.fbq) {
            windowGlobal.fbq('track', 'TicketDetails-Submit-FTX2021');
          }

          break;
        }

        case 3: {
          trackCustomEvent({
            category: 'Success_FtX2021',
            action: 'clickclose',
            label: 'FTX2021',
          });
          if (windowGlobal.lintrk) {
            windowGlobal.lintrk('track', { conversion_id: 5426468 });
          }
          if (windowGlobal.fbq) {
            windowGlobal.fbq('track', 'Success_ClickClose_FTX2021');
          }

          break;
        }

        default:
          break;
      }

      if (currentStep === 3) {
        setOpenTicketModal(false);
      } else {
        setCloseConfirm((state) => !state);
      }

      // setOpenTicketModal(false);
    },
    [currentStep],
  );

  const gotoNextStep = useCallback(() => {
    setCurrentStep((state) => state + 1);
  }, []);

  const gotoPrevStep = useCallback(() => {
    if (currentStep === 2 && currentIndex > 0) {
      setCurrentIndex((state) => (state === 0 ? state : state - 1));
    } else {
      setCurrentStep((state) => (state === 1 ? state : state - 1));
    }
  }, [currentStep, currentIndex]);

  const supportEvent = useCallback(() => {
    // Success/Ticket booked
    trackCustomEvent({
      category: 'Success_FtX2021',
      action: 'emailclick',
      label: 'FTX2021',
    });

    if (windowGlobal.lintrk) {
      windowGlobal.lintrk('track', { conversion_id: 5426452 });
    }

    if (windowGlobal.fbq) {
      windowGlobal.fbq('track', 'Success_ClickFTXemail_FTX2021');
    }
  }, []);
  useEffect(() => {
    if (!openTicketModal) {
      reset();
      setCurrentStep(1);
      setCurrentIndex(0);
    }
  }, [openTicketModal]);

  useEffect(() => {
    if (location.search.includes('coupon_code')) {
      // setOpenTicketModal(0);
    }
  }, [location]);

  const renderValidationErrors = useCallback(
    (input) =>
      getErrors(input).map(({ message }, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <small key={i}>{message}</small>
      )),
    [getErrors],
  );

  const closeEarlyModal = useCallback(() => {
    setOpenTicketModal(0);
    setCloseConfirm(false);
  }, []);

  const UndoCloseEarlyModal = useCallback(() => {
    setCloseConfirm(false);
  }, []);

  return (
    <div className={`ticket-modal-main ${openTicketModal ? 'show' : ''}`}>
      <div className="modal-block">
        <button type="button" className="close-popup" onClick={closeModalClick}>
          &nbsp;
        </button>

        <div className="modal-header">
          <img
            src="https://ftx-rp.netlify.app/img/ftx-logo.png"
            alt="FTX 2021"
          />
          <p>9th - 10th December ‘21</p>
        </div>

        <div className={`close-confirm-block ${closeConfirm ? 'show' : ''}`}>
          <p>
            Are you sure you want to leave without your{' '}
            <span className="bold">FREE</span> ticket?
          </p>

          <div className="close-button-block flex">
            <Button onClick={closeEarlyModal}>YES</Button>
            <Button onClick={UndoCloseEarlyModal} className="hover-reversed">
              NO
            </Button>
          </div>
        </div>

        <p
          className={`early-modal-header ${currentStep !== 1 ? 'hidden' : ''}`}>
          Awesome choice! You just got a <span className="bold">FREE</span>{' '}
          ticket
        </p>

        <div
          className={`participants-counter-block ${
            currentStep !== 1 ? 'hidden' : ''
          }`}>
          <p>Select Tickets</p>
          <div className="participants-couter">
            <button
              className="minus-btn"
              type="button"
              disabled={tickets.length === 1}
              onClick={decrementCount}>
              &nbsp;
            </button>
            <input type="number" min="1" value={tickets.length} readOnly />
            <button
              type="button"
              disabled={tickets.length === 10}
              onClick={incrementCount}>
              &nbsp;
            </button>
          </div>
        </div>

        <div className="participants-modal-body">
          {/* step 1 */}
          <div
            className={`step step-1 center ${currentStep === 1 ? 'show' : ''}`}>
            <p>Please select total number of tickets</p>
            <Button className="shaky" onClick={onSubmit}>
              BOOK YOUR TICKET
            </Button>
          </div>
          <div className={`step step-2 ${currentStep === 2 ? 'show' : ''}`}>
            <div className="ticket-details-main">
              <button
                style={{
                  display: (currentStep === 2 ? currentIndex > 0 : true)
                    ? 'block'
                    : 'none',
                }}
                type="button"
                onClick={gotoPrevStep}
                className="button-unstyled back-button">
                <svg fill="#8336F2" viewBox="0 0 20 16">
                  <path d="M.293 7.293a1 1 0 0 0 0 1.414l6.364 6.364a1 1 0 0 0 1.414-1.414L2.414 8l5.657-5.657A1 1 0 0 0 6.657.93L.293 7.293ZM19.667 7H1v2h18.667V7Z" />
                </svg>
              </button>
              <p>Enter Ticket {currentIndex + 1} details</p>
              <form onSubmit={onSubmit}>
                <div className="form-group">
                  <input
                    type="text"
                    name={`tickets[${currentIndex}].name`}
                    value={tickets[currentIndex].name}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Enter Name"
                  />
                  <img
                    src="https://ftx-rp.netlify.app/img/form-error.svg"
                    className="error"
                    alt="error"
                    style={{
                      display: getErrors(`tickets[${currentIndex}].name`).length
                        ? 'block'
                        : 'none',
                    }}
                  />
                  {renderValidationErrors(`tickets[${currentIndex}].name`)}
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name={`tickets[${currentIndex}].phoneNumber`}
                    value={tickets[currentIndex].phoneNumber}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Contact Number"
                  />
                  <img
                    src="https://ftx-rp.netlify.app/img/form-error.svg"
                    className="error"
                    alt="error"
                    style={{
                      display: getErrors(`tickets[${currentIndex}].phoneNumber`)
                        .length
                        ? 'block'
                        : 'none',
                    }}
                  />
                  {renderValidationErrors(
                    `tickets[${currentIndex}].phoneNumber`,
                  )}
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    name={`tickets[${currentIndex}].email`}
                    value={tickets[currentIndex].email}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Email"
                  />
                  <img
                    src="https://ftx-rp.netlify.app/img/form-error.svg"
                    className="error"
                    alt="error"
                    style={{
                      display: getErrors(`tickets[${currentIndex}].email`)
                        .length
                        ? 'block'
                        : 'none',
                    }}
                  />
                  {renderValidationErrors(`tickets[${currentIndex}].email`)}
                </div>
                <div className="center">
                  <Button type="submit" disabled={loading}>
                    {tickets.length > 1 ? 'NEXT' : 'SUBMIT'}
                  </Button>
                </div>
              </form>
            </div>
            <p className="error-msg" style={{ display: 'none' }}>
              Please enter corresponding detail correctly for the participant!
            </p>
          </div>
          <div className={`step step-3 ${currentStep === 3 ? 'show' : ''}`}>
            <div className="participants-counter-block">
              <p className="center" style={{ maxWidth: 600, margin: '0 auto' }}>
                You're all set for FTX! Your tickets will be sent to your
                registered email IDs. Do reach out to us at{' '}
                <a
                  style={{ color: '#fff' }}
                  onClick={supportEvent}
                  href="mailto:ftx@razorpay.com">
                  ftx@razorpay.com
                </a>{' '}
                for any further queries.
              </p>
            </div>
            <div className="confirmation-wrapper">
              <div className="confirmation-block">
                {tickets.map(({ name, email, phoneNumber }, i) => (
                  <div
                    /* eslint-disable-next-line react/no-array-index-key */
                    key={i}
                    className="col-6 col"
                    style={{
                      transform: `translateX(-${sliderActive * 100}%)`,
                    }}>
                    <div className="single-confirmation">
                      <p className="bold">{name}</p>
                      <p>{email}</p>
                      <p>{phoneNumber}</p>
                    </div>
                  </div>
                ))}
              </div>
              {tickets.length > (isMobile ? 1 : 2) && (
                <div className="confirmation-arrows">
                  <button
                    onClick={sliderClickHandler}
                    data-type="prev"
                    type="button"
                    className="button-unstyled prev">
                    <svg fill="#8336F2" viewBox="0 0 20 16">
                      <path d="M.293 7.293a1 1 0 0 0 0 1.414l6.364 6.364a1 1 0 0 0 1.414-1.414L2.414 8l5.657-5.657A1 1 0 0 0 6.657.93L.293 7.293ZM19.667 7H1v2h18.667V7Z" />
                    </svg>
                  </button>
                  <button
                    onClick={sliderClickHandler}
                    data-type="next"
                    type="button"
                    className="button-unstyled next">
                    <svg fill="#8336F2" viewBox="0 0 20 16">
                      <path d="M19.374 8.707a1 1 0 0 0 0-1.414L13.01.929a1 1 0 1 0-1.414 1.414L17.253 8l-5.657 5.657a1 1 0 0 0 1.414 1.414l6.364-6.364ZM0 9h18.667V7H0v2Z" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

EarlyAccessModal.propTypes = {
  location: PropTypes.object.isRequired,
  openTicketModal: PropTypes.bool.isRequired,
  setOpenTicketModal: PropTypes.func.isRequired,
};

export default EarlyAccessModal;
